import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import AuthImage from "@/components/AuthImage";
import { Edit3, ArrowLeft } from "lucide-react";

export default function BoatDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [boat, setBoat] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get(`/boats/${id}`).then(({ data }) => setBoat(data)).catch(() => navigate("/mis-embarcaciones"));
  }, [id, navigate]);

  if (!boat) return <div className="max-w-6xl mx-auto px-6 py-24 text-slate-400">·</div>;
  const primaryIdx = Math.max(0, (boat.images || []).findIndex((i) => i.is_primary));
  const gallery = boat.images || [];
  const displayed = gallery[active] || gallery[primaryIdx] || null;
  const ev = Number(boat.estimated_value || 0);
  const sharePrice = Math.round((ev / 4) * 100) / 100;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <div className="flex items-center justify-between gap-4">
        <Link to="/mis-embarcaciones" className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="boat-detail-back">
          <ArrowLeft className="h-4 w-4" /> {t.boats.title}
        </Link>
        <Link to={`/mis-embarcaciones/${id}/editar`} className="btn-outline text-sm inline-flex items-center gap-2" data-testid="boat-detail-edit">
          <Edit3 className="h-4 w-4" /> {t.boats.edit}
        </Link>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="aspect-[16/10] rounded-[20px] overflow-hidden bg-slate-100" data-testid="boat-gallery-main">
            {displayed ? <AuthImage path={displayed.storage_path} className="w-full h-full object-cover" /> :
              <div className="w-full h-full grid place-items-center text-slate-400">— no photos —</div>}
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-3">
              {gallery.map((img, i) => (
                <button key={img.id} onClick={() => setActive(i)}
                  className={`aspect-[4/3] rounded-lg overflow-hidden border-2 transition-colors ${i === active ? "border-[#0B192C]" : "border-transparent"}`}
                  data-testid={`gallery-thumb-${i}`}>
                  <AuthImage path={img.storage_path} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-4">
          <p className="eyebrow">{boat.type ? t.boats.types[boat.type] : ""}</p>
          <h1 className="font-serif text-3xl lg:text-4xl mt-2 text-[#0B192C]" data-testid="boat-detail-title">{boat.brand} {boat.model}</h1>
          <p className="mt-1 text-slate-500 text-sm">{boat.year} · {boat.location} {boat.port ? `· ${boat.port}` : ""}</p>

          <div className="mt-8 rounded-2xl border border-slate-100 bg-[#F8F9FA] p-6">
            <p className="eyebrow">{t.boats.section.ownership}</p>
            <p className="mt-1 font-serif text-xl text-[#0B192C]">{t.boats.ownership.standardDesc}</p>
            <div className="mt-5 flex items-center gap-1 h-2">
              {[0,1,2,3].map((i) => <div key={i} className="flex-1 rounded-full bg-[#C2A878]/90 h-full" />)}
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div><p className="text-[10px] font-display uppercase tracking-widest text-slate-400">{t.boats.ownership.boatValue}</p>
                <p className="mt-1 font-serif text-lg text-[#0B192C]" data-testid="detail-boat-value">€{ev.toLocaleString()}</p></div>
              <div><p className="text-[10px] font-display uppercase tracking-widest text-slate-400">{t.boats.ownership.sharePrice}</p>
                <p className="mt-1 font-serif text-lg text-[#0B192C]" data-testid="detail-share-price">€{sharePrice.toLocaleString()}</p></div>
              <div><p className="text-[10px] font-display uppercase tracking-widest text-slate-400">{t.boats.ownership.owners}</p>
                <p className="mt-1 font-serif text-lg text-slate-700">4</p></div>
              <div><p className="text-[10px] font-display uppercase tracking-widest text-slate-400">{t.boats.ownership.percentage}</p>
                <p className="mt-1 font-serif text-lg text-slate-700">25%</p></div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-16 grid lg:grid-cols-2 gap-10">
        <section>
          <h2 className="font-display text-xs tracking-[0.24em] uppercase text-slate-400 mb-4">{t.boats.section.basic}</h2>
          <dl className="divide-y divide-slate-100 border-t border-slate-100" data-testid="detail-specs">
            {[
              [t.boats.f.type, t.boats.types[boat.type]],
              [t.boats.f.length, boat.length ? `${boat.length} m` : "—"],
              [t.boats.f.beam, boat.beam ? `${boat.beam} m` : "—"],
              [t.boats.f.capacity, boat.capacity || "—"],
              [t.boats.f.engine, boat.engine || "—"],
              [t.boats.f.year, boat.year || "—"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-3 text-sm">
                <dt className="text-slate-500">{k}</dt>
                <dd className="font-display text-[#0B192C]">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section>
          <h2 className="font-display text-xs tracking-[0.24em] uppercase text-slate-400 mb-4">{t.boats.section.economic}</h2>
          <dl className="divide-y divide-slate-100 border-t border-slate-100" data-testid="detail-economic">
            {[
              [t.boats.f.estimatedValue, `€${Number(boat.estimated_value || 0).toLocaleString()}`],
              [t.boats.f.annualCost, `€${Number(boat.annual_cost || 0).toLocaleString()}`],
              [t.boats.f.mooringCost, `€${Number(boat.mooring_cost || 0).toLocaleString()}`],
              [t.boats.f.otherCosts, `€${Number(boat.other_costs || 0).toLocaleString()}`],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-3 text-sm">
                <dt className="text-slate-500">{k}</dt>
                <dd className="font-display text-[#0B192C]">{v}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      {(boat.description || boat.general_condition || boat.last_maintenance || boat.next_maintenance) && (
        <section className="mt-14">
          <h2 className="font-display text-xs tracking-[0.24em] uppercase text-slate-400 mb-4">{t.boats.section.condition}</h2>
          <div className="rounded-2xl border border-slate-100 p-6">
            {boat.general_condition && (
              <p className="text-sm text-slate-600"><span className="text-slate-400">{t.boats.f.generalCondition}: </span>{t.boats.conditions[boat.general_condition]}</p>
            )}
            {boat.last_maintenance && <p className="text-sm text-slate-600 mt-2"><span className="text-slate-400">{t.boats.f.lastMaintenance}: </span>{boat.last_maintenance}</p>}
            {boat.next_maintenance && <p className="text-sm text-slate-600 mt-1"><span className="text-slate-400">{t.boats.f.nextMaintenance}: </span>{boat.next_maintenance}</p>}
            {boat.description && <p className="text-slate-600 leading-relaxed mt-4">{boat.description}</p>}
          </div>
        </section>
      )}
    </div>
  );
}
